import { Flex } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import { Client } from 'src/users';
import { Family } from 'src/clients/hooks/useFamily';
import { useGenerateFactFind } from 'src/fact-find/hooks/useGenerateFactFind';
import { SettingsRow } from 'src/suitability-report/SettingsRow';

type FactFindData = {
  client?: Client;
  family?: Family;
  selectionType?: 'client' | 'family';
  includeSensitiveFields?: boolean;
};

export default function GenerateOptionsStep(props: StepProps<FactFindData>) {
  const { data, setData } = props;
  const { generateFactFind, isLoading } = useGenerateFactFind();
  const queryClient = useQueryClient();

  const handleToggle = (value: boolean) => {
    setData({ ...data, includeSensitiveFields: value });
  };

  const handleGenerate = async () => {
    try {
      const clientIdToInvalidate =
        data.selectionType === 'client' ? data.client?.id : data.family?.mainFamilyMember.clientId;

      if (data.selectionType === 'client' && data.client) {
        await generateFactFind('client', data.client.id, {
          include_sensitive_fields: Boolean(data.includeSensitiveFields),
        });
      } else if (data.selectionType === 'family' && data.family) {
        await generateFactFind('family', data.family.id, {
          include_sensitive_fields: Boolean(data.includeSensitiveFields),
        });
      } else {
        notifyAndLogError(
          'Error',
          'Failed to generate fact find. A client or family must be selected.',
          new Error('No client or family selected'),
        );
      }

      if (clientIdToInvalidate) {
        queryClient.invalidateQueries({
          predicate: query =>
            query.queryKey.some(
              key =>
                typeof key === 'string' &&
                key.includes(`clients/${clientIdToInvalidate}/fact-finds`),
            ),
        });
      }
      props.onSuccess();
    } catch (error) {
      notifyAndLogError('Error', 'Failed to generate fact find', error);
    }
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Generate"
      description="Configure settings and generate fact find"
      continueText={isLoading ? 'Generating fact find' : 'Generate Fact Find'}
      canGoForward={!isLoading}
      canGoBack={!isLoading}
      nextStep={handleGenerate}
      isSubmitting={isLoading}
    >
      <Flex direction="column" gap="md">
        <SettingsList>
          <SettingsRow
            title="Show sensitive fields"
            description="Whether to include sensitive fields such as addiction history, mental health considerations, and caring responsibilities in the fact find."
            checked={Boolean(props.data.includeSensitiveFields)}
            onChange={handleToggle}
          />
        </SettingsList>
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

type SettingsListProps = {
  children: React.ReactNode;
};

function SettingsList({ children }: SettingsListProps) {
  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12 }}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12 }}
        bg="white"
      >
        {children}
      </Flex>
    </Flex>
  );
}
